import * as THREE from 'three';
import { assetReady } from './assetEvents';
import { ResourcePool } from './ResourcePool';
import { viewHeadingOffset, windowViewFiles, type WindowView } from '../../state/windowViews';
import type { Vec2 } from '../../core/hp/types';

/**
 * The outdoors round the house: one panorama (`src/state/windowViews.ts`) seen through the windows from inside and
 * all round from outside, the way archviz tools and games draw an environment: a background at infinity and a real
 * ground under the house.
 *
 * - The background reads the panorama by the view ray's own direction, never projected: from any room and any floor it
 *   is the photograph itself, as sharp as its pixels, with the horizon at eye level.
 * - The ground is a mesh at the height of the lowest floor. Near the house it wears the view's own ground (`ground.webp`,
 *   cut next to the panorama's tripod: grass, sand, paving), so the ground outside a window has its perspective and
 *   parallax and the house stands on something; the scene's sun throws the house's real shadow on it (a shadow catcher
 *   over it) and the light goes where the walls meet it. Towards the horizon the mesh turns into the panorama's own far
 *   ground, read by direction like the background, so where it ends nothing shows.
 *
 * The background is drawn after the opaque house at the far plane without depth, and the ground just before it, so a
 * room pays only for its windows; glass blends over both.
 */

/** A decoded picture ready to upload: an ImageBitmap in the browser, anything sized in tests. */
export type OutdoorPicture = TexImageSource | { width: number; height: number };
export type OutdoorPictureLoader = (url: string, signal: AbortSignal) => Promise<OutdoorPicture>;
/** idle: nothing is wanted or shown yet; loading: the wanted view has not appeared; ready: it has; error: it failed. */
export type OutdoorStatus = 'idle' | 'loading' | 'ready' | 'error';
/** What the shown view is drawn from: its preview until the full panorama is in. */
export type OutdoorQuality = 'preview' | 'full' | null;

/** Fetches a picture and decodes it off the main thread, as stored (sRGB, top row first). */
export async function fetchOutdoorPicture(url: string, signal: AbortSignal): Promise<ImageBitmap> {
  const response = await fetch(url, { signal });
  if (!response.ok) throw new Error(`${url}: HTTP ${response.status}`);
  return createImageBitmap(await response.blob(), { colorSpaceConversion: 'none' });
}

export interface OutdoorOptions {
  loader?: OutdoorPictureLoader;
  /** The renderer's largest anisotropy: the ground is seen at grazing angles. */
  anisotropy?: number;
  /** A frame is wanted: a picture came in, or a cross-fade is under way. */
  onChange?: () => void;
  onStatus?: (status: OutdoorStatus) => void;
}

const FADE_MS = 180;
const FOOTPRINT_SIZE = 256;
/** Metres round the house the footprint mask reaches. */
const FOOTPRINT_MARGIN = 6;
/** The ground mesh reaches past the camera's far plane: it is cut there, after it has turned into the background. */
const GROUND_REACH = 1500;
/** Where the ground turns into the panorama's own far ground: between these angles under the horizon (radians)… */
const HORIZON_BAND: [number, number] = [1.2 * Math.PI / 180, 4 * Math.PI / 180];
/** …and at the latest between these distances from the camera, well inside its 400 m far plane. */
const FAR_BAND: [number, number] = [200, 330];
/** How dark the house's sun shadow falls on the ground, by the kind of sun in the panorama… */
const SHADOW_OPACITY = { clear: .42, soft: .26, none: .16 } as const;
/** …and how much of the sky's colour it takes: shade out of doors is lit by the sky alone. */
const SHADOW_TINT = .04;

interface Held { texture: THREE.Texture; release: () => void }

export class OutdoorView {
  readonly root = new THREE.Group();
  private readonly mesh: THREE.Mesh<THREE.BoxGeometry, THREE.ShaderMaterial>;
  private readonly ground: THREE.Mesh<THREE.PlaneGeometry, THREE.ShaderMaterial>;
  private readonly shadow: THREE.Mesh<THREE.PlaneGeometry, THREE.ShadowMaterial>;
  private readonly uniforms: Record<string, THREE.IUniform>;
  private readonly blank: THREE.DataTexture;
  private readonly clear: THREE.DataTexture;
  private readonly loader: OutdoorPictureLoader;
  private readonly anisotropy: number;
  private readonly onChange: () => void;
  private readonly onStatus: (status: OutdoorStatus) => void;
  private footprintTexture: THREE.DataTexture | null = null;
  /** What is wanted, and what is drawn now (possibly an older view until the wanted one's preview is in). */
  private wanted: WindowView | null = null;
  private rotation = 0;
  private drawn: { view: WindowView; panorama: Held; quality: 'preview' | 'full'; ground: Held | null } | null = null;
  private fading: Held | null = null;
  private fadeStart = 0;
  private loading: { view: WindowView; controller: AbortController } | null = null;
  private shown = false;
  private disposed = false;
  private currentStatus: OutdoorStatus = 'idle';

  constructor(private readonly resources = new ResourcePool(), options: OutdoorOptions = {}) {
    this.loader = options.loader ?? fetchOutdoorPicture;
    this.anisotropy = Math.max(1, options.anisotropy ?? 1);
    this.onChange = options.onChange ?? assetReady;
    this.onStatus = options.onStatus ?? (() => {});
    this.blank = solidTexture([128, 128, 128, 255], THREE.NoColorSpace);
    this.clear = solidTexture([0, 0, 0, 255], THREE.NoColorSpace);
    this.uniforms = {
      panorama: { value: this.blank }, previous: { value: this.blank }, fade: { value: 1 },
      heading: { value: 0 }, previousHeading: { value: 0 },
      ground: { value: this.blank }, groundSize: { value: 1.6 }, groundReady: { value: 0 },
      footprint: { value: this.clear }, footprintRect: { value: new THREE.Vector4(0, 0, 1, 1) },
      horizonBand: { value: new THREE.Vector2(...HORIZON_BAND) }, farBand: { value: new THREE.Vector2(...FAR_BAND) },
    };
    const material = new THREE.ShaderMaterial({
      name: 'OutdoorView', uniforms: this.uniforms, vertexShader: SKY_VERTEX, fragmentShader: COMMON + SKY_FRAGMENT,
      side: THREE.BackSide, depthWrite: false, toneMapped: false, fog: false,
    });
    this.mesh = new THREE.Mesh(new THREE.BoxGeometry(2, 2, 2), material);
    this.mesh.name = 'outdoor-view';
    // Never culled or sorted by distance: it surrounds the camera, and it goes after the opaque house.
    this.mesh.frustumCulled = false; this.mesh.renderOrder = 1000;
    this.mesh.onBeforeRender = () => this.stepFade();
    this.mesh.userData.noContactShadows = true;
    // The ground: opaque, writing depth, so ambient occlusion sees the walls meet it; after the house, before the sky.
    const plane = new THREE.PlaneGeometry(GROUND_REACH * 2, GROUND_REACH * 2).rotateX(-Math.PI / 2);
    this.ground = new THREE.Mesh(plane, new THREE.ShaderMaterial({
      name: 'OutdoorGround', uniforms: this.uniforms, vertexShader: GROUND_VERTEX, fragmentShader: COMMON + GROUND_FRAGMENT,
      toneMapped: false, fog: false,
    }));
    this.ground.name = 'outdoor-ground'; this.ground.renderOrder = 999; this.ground.frustumCulled = false;
    // The house's sun shadow on it; drawn before the glass, which blends over it.
    this.shadow = new THREE.Mesh(plane, new THREE.ShadowMaterial({ opacity: SHADOW_OPACITY.clear, depthWrite: false, toneMapped: false }));
    this.shadow.name = 'outdoor-shadow'; this.shadow.receiveShadow = true; this.shadow.renderOrder = -1; this.shadow.frustumCulled = false;
    this.shadow.userData.noContactShadows = true;
    this.root.name = 'outdoor-view-root';
    this.root.add(this.mesh, this.ground, this.shadow);
    this.root.visible = false;
  }

  get status(): OutdoorStatus { return this.currentStatus; }
  /** The view drawn now and from what: for checks and the picker. */
  state(): { wanted: string | null; drawn: string | null; quality: OutdoorQuality; status: OutdoorStatus; visible: boolean; rotation: number; ground: number } {
    return { wanted: this.wanted?.id ?? null, drawn: this.drawn?.view.id ?? null, quality: this.drawn?.quality ?? null,
      status: this.currentStatus, visible: this.root.visible, rotation: this.rotation, ground: this.groundY };
  }

  /**
   * The view to draw, turned by `rotation` degrees. Nothing is asked for while the outdoors is not on screen: a
   * project that is only planned downloads no view. A new view keeps the old one drawn until its preview is in.
   */
  setView(view: WindowView | null, rotation: number): void {
    if (this.disposed) return;
    this.rotation = rotation;
    const changed = view?.id !== this.wanted?.id;
    this.wanted = view;
    this.applyHeadings();
    if (!view) {
      this.cancelLoading(); this.dropDrawn(); this.setStatus('idle');
    } else if (changed) {
      this.cancelLoading();
      if (this.drawn?.view.id === view.id) this.setStatus('ready');
      else { this.setStatus('loading'); if (this.shown) this.load(view); }
    }
    this.syncVisibility(); this.onChange();
  }

  /**
   * Whether the 3D view shows the outdoors now. The viewer says so on every rebuild and camera change, so pictures
   * are asked for only when it goes from hidden to shown: the first time, or again after one failed.
   */
  setVisible(visible: boolean): void {
    if (this.disposed) return;
    if (visible && !this.shown) {
      this.shown = true;
      const view = this.wanted;
      if (view && !this.loading && (this.drawn?.view.id !== view.id || this.drawn.quality !== 'full' || !this.drawn.ground)) this.load(view);
    } else if (!visible) this.shown = false;
    this.syncVisibility();
  }

  /** Asks again for a view whose pictures failed. */
  retry(): void {
    if (this.wanted && !this.loading && this.shown) this.load(this.wanted);
  }

  private groundY = 0;
  /** The ground under the house's middle, at the height of the lowest floor. A few centimetres under it, so the
   * floors never fight it for depth; the footprint cuts it away under the house anyway. */
  fit(centre: Vec2, groundY: number): void {
    if (this.groundY === groundY && this.ground.position.x === centre.x && this.ground.position.z === centre.z) return;
    this.groundY = groundY;
    this.ground.position.set(centre.x, groundY - .03, centre.z);
    this.shadow.position.set(centre.x, groundY - .025, centre.z);
    this.ground.updateMatrixWorld(); this.shadow.updateMatrixWorld();
    this.onChange();
  }

  /**
   * The house's footprint on the ground: the ground is cut away under it, and a little darker where the walls meet it
   * (the contact the ambient occlusion also draws at rest). The sun's own shadow comes from the shadow catcher.
   */
  setFootprint(shapes: readonly (readonly Vec2[])[]): void {
    if (this.disposed) return;
    const rect = footprintRect(shapes);
    if (!rect) { this.uniforms.footprint.value = this.clear; this.onChange(); return; }
    const mask = footprintMask(shapes, rect, FOOTPRINT_SIZE, .3);
    let texture = this.footprintTexture;
    if (!texture || texture.image.width !== FOOTPRINT_SIZE) {
      texture?.dispose();
      texture = new THREE.DataTexture(mask, FOOTPRINT_SIZE, FOOTPRINT_SIZE, THREE.RedFormat, THREE.UnsignedByteType);
      texture.minFilter = texture.magFilter = THREE.LinearFilter; texture.generateMipmaps = false;
      texture.wrapS = texture.wrapT = THREE.ClampToEdgeWrapping;
      this.footprintTexture = texture;
    } else (texture.image.data as Uint8Array).set(mask);
    texture.needsUpdate = true;
    this.uniforms.footprint.value = texture;
    (this.uniforms.footprintRect.value as THREE.Vector4).set(rect.x0, rect.z0, 1 / (rect.x1 - rect.x0), 1 / (rect.z1 - rect.z0));
    this.onChange();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.cancelLoading(); this.dropDrawn();
    this.root.removeFromParent();
    this.mesh.geometry.dispose(); this.mesh.material.dispose();
    this.ground.geometry.dispose(); this.ground.material.dispose(); this.shadow.material.dispose();
    this.blank.dispose(); this.clear.dispose(); this.footprintTexture?.dispose();
  }

  private setStatus(status: OutdoorStatus): void {
    if (status === this.currentStatus) return;
    this.currentStatus = status; this.onStatus(status);
  }

  private syncVisibility(): void {
    // A map without its picture draws black: until a picture is in, the scene's background stands in.
    this.root.visible = this.shown && !!this.wanted && !!this.drawn;
  }

  private applyHeadings(): void {
    const radians = Math.PI / 180;
    const view = this.drawn?.view ?? this.wanted;
    if (view) this.uniforms.heading.value = viewHeadingOffset(view, this.rotation) * radians;
    if (this.wanted && this.drawn) {
      this.uniforms.groundSize.value = this.drawn.view.ground.size;
      this.uniforms.groundReady.value = this.drawn.ground ? 1 : 0;
      this.shadow.material.opacity = SHADOW_OPACITY[(this.drawn.view.sun?.kind ?? 'none') as keyof typeof SHADOW_OPACITY] ?? SHADOW_OPACITY.soft;
      this.shadow.material.color.set(this.drawn.view.colours.sky).multiplyScalar(SHADOW_TINT);
    }
  }

  private cancelLoading(): void {
    this.loading?.controller.abort(); this.loading = null;
  }

  private dropDrawn(): void {
    this.fading?.release(); this.fading = null;
    if (this.drawn) { this.drawn.panorama.release(); this.drawn.ground?.release(); this.drawn = null; }
    this.uniforms.panorama.value = this.uniforms.previous.value = this.uniforms.ground.value = this.blank;
    this.uniforms.fade.value = 1; this.uniforms.groundReady.value = 0;
  }

  private hold(picture: OutdoorPicture, url: string, kind: 'panorama' | 'tile'): Held {
    const texture = new THREE.Texture(picture as TexImageSource);
    // An ImageBitmap goes up as it is stored, top row first; the shader reads it that way.
    texture.flipY = false;
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = kind === 'panorama' ? THREE.ClampToEdgeWrapping : THREE.RepeatWrapping;
    texture.anisotropy = this.anisotropy;
    texture.userData.imageUrl = url;
    texture.needsUpdate = true;
    this.resources.register(texture, [texture, texture.source]);
    const release = this.resources.retain([texture]);
    return { texture, release: () => { release(); this.resources.remove(texture); } };
  }

  /**
   * The preview first, unless this view is drawn already, then the full panorama; the ground tile alongside.
   * What is not in by the end is asked for again the next time the outdoors comes into view.
   */
  private load(view: WindowView): void {
    const controller = new AbortController(), signal = controller.signal;
    this.loading = { view, controller };
    const files = windowViewFiles(view);
    const current = () => !signal.aborted && !this.disposed && this.loading?.controller === controller;
    const drawnHere = () => this.drawn?.view.id === view.id ? this.drawn : null;
    const ask = (url: string): Promise<OutdoorPicture | null> => this.loader(url, signal)
      .then(picture => { if (current()) return picture; closePicture(picture); return null; }, () => null);
    void (async () => {
      const ground = drawnHere()?.ground ? Promise.resolve(null) : ask(files.ground);
      if (!drawnHere()) {
        const preview = await ask(files.preview);
        if (preview && current() && !drawnHere()) this.draw(view, this.hold(preview, files.preview, 'panorama'), 'preview');
      }
      const full = current() && drawnHere()?.quality !== 'full' ? await ask(files.panorama) : null;
      if (full && current()) this.draw(view, this.hold(full, files.panorama, 'panorama'), 'full');
      const groundPicture = await ground;
      if (!current()) { if (groundPicture) closePicture(groundPicture); return; }
      const shown = drawnHere();
      if (groundPicture && shown && !shown.ground) {
        shown.ground = this.hold(groundPicture, files.ground, 'tile');
        this.uniforms.ground.value = shown.ground.texture;
        this.applyHeadings();
      } else if (groundPicture) closePicture(groundPicture);
      this.loading = null;
      // A view with nothing of it in is an error; one drawn from its preview is shown and finished later.
      this.setStatus(shown ? 'ready' : 'error');
      this.syncVisibility(); this.onChange();
    })();
  }

  /** Draws a picture of `view`: the preview cross-fades from what was drawn, the full panorama replaces its preview. */
  private draw(view: WindowView, panorama: Held, quality: 'preview' | 'full'): void {
    const previous = this.drawn;
    if (previous?.view.id === view.id) {
      previous.panorama.release(); previous.panorama = panorama; previous.quality = quality;
      this.uniforms.panorama.value = panorama.texture;
    } else {
      this.fading?.release(); this.fading = null;
      if (previous) {
        // The old view fades out under the new one, unless motion is to be reduced.
        previous.ground?.release();
        if (reducedMotion()) previous.panorama.release();
        else { this.fading = previous.panorama; this.uniforms.previousHeading.value = this.uniforms.heading.value; }
      }
      this.drawn = { view, panorama, quality, ground: null };
      this.uniforms.panorama.value = panorama.texture;
      this.uniforms.previous.value = this.fading?.texture ?? this.blank;
      this.uniforms.fade.value = this.fading ? 0 : 1;
      this.fadeStart = typeof performance === 'undefined' ? 0 : performance.now();
    }
    this.applyHeadings();
    this.syncVisibility(); this.onChange();
  }

  private stepFade(): void {
    if (!this.fading) return;
    const t = Math.min(1, (performance.now() - this.fadeStart) / FADE_MS);
    this.uniforms.fade.value = t * t * (3 - 2 * t);
    if (t >= 1) {
      this.fading.release(); this.fading = null;
      this.uniforms.previous.value = this.blank; this.uniforms.fade.value = 1;
    }
    this.onChange();
  }
}

/**
 * The shader's panorama coordinates of a world direction, for checks: u grows clockwise on the plan from the
 * panorama's middle, which faces the plan heading `heading` (radians); v is 0 at the zenith.
 */
export function panoramaUv(direction: readonly [number, number, number], heading: number): [number, number] {
  const [x, y, z] = direction, length = Math.hypot(x, y, z) || 1;
  const u = (((Math.atan2(x, -z) - heading) / (2 * Math.PI) + .5) % 1 + 1) % 1;
  return [u, .5 - Math.asin(Math.max(-1, Math.min(1, y / length))) / Math.PI];
}

function closePicture(picture: OutdoorPicture): void {
  if ('close' in picture && typeof picture.close === 'function') picture.close();
}

function reducedMotion(): boolean {
  try { return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

function solidTexture(rgba: number[], colorSpace: THREE.ColorSpace): THREE.DataTexture {
  const texture = new THREE.DataTexture(new Uint8Array(rgba), 1, 1, THREE.RGBAFormat, THREE.UnsignedByteType);
  texture.colorSpace = colorSpace; texture.needsUpdate = true;
  return texture;
}

/** The plan rectangle the footprint texture covers: the shapes and a margin for the shadow. */
export function footprintRect(shapes: readonly (readonly Vec2[])[]): { x0: number; z0: number; x1: number; z1: number } | null {
  let x0 = Infinity, z0 = Infinity, x1 = -Infinity, z1 = -Infinity;
  for (const shape of shapes) for (const p of shape) { x0 = Math.min(x0, p.x); z0 = Math.min(z0, p.z); x1 = Math.max(x1, p.x); z1 = Math.max(z1, p.z); }
  if (!Number.isFinite(x0) || x1 - x0 < .01 || z1 - z0 < .01) return null;
  // Square texels: the longer side sets the scale.
  const half = Math.max(x1 - x0, z1 - z0) / 2 + FOOTPRINT_MARGIN, cx = (x0 + x1) / 2, cz = (z0 + z1) / 2;
  return { x0: cx - half, z0: cz - half, x1: cx + half, z1: cz + half };
}

/**
 * The shapes filled into a `size`² mask over `rect` (rows run along +z), blurred by about `blur` metres: 255 under
 * the house, fading to 0 round it.
 */
export function footprintMask(shapes: readonly (readonly Vec2[])[], rect: { x0: number; z0: number; x1: number; z1: number }, size: number, blur: number): Uint8Array {
  const field = new Float32Array(size * size);
  const sx = size / (rect.x1 - rect.x0), sz = size / (rect.z1 - rect.z0);
  for (const shape of shapes) {
    if (shape.length < 3) continue;
    const points = shape.map(p => [(p.x - rect.x0) * sx, (p.z - rect.z0) * sz]);
    for (let row = 0; row < size; row++) {
      const y = row + .5, crossings: number[] = [];
      for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
        const [ax, ay] = points[i], [bx, by] = points[j];
        if ((ay > y) !== (by > y)) crossings.push(ax + (y - ay) / (by - ay) * (bx - ax));
      }
      crossings.sort((a, b) => a - b);
      for (let k = 0; k + 1 < crossings.length; k += 2) {
        const from = Math.max(0, Math.ceil(crossings[k] - .5)), to = Math.min(size - 1, Math.floor(crossings[k + 1] - .5));
        for (let col = from; col <= to; col++) field[row * size + col] = 1;
      }
    }
  }
  // Three box passes approximate a Gaussian of the given width.
  const radius = Math.max(1, Math.round(blur * sx / 1.7));
  const scratch = new Float32Array(size * size);
  for (let pass = 0; pass < 3; pass++) { boxBlur(field, scratch, size, radius, true); boxBlur(scratch, field, size, radius, false); }
  const out = new Uint8Array(size * size);
  for (let i = 0; i < out.length; i++) out[i] = Math.round(Math.min(1, Math.max(0, field[i])) * 255);
  return out;
}

function boxBlur(from: Float32Array, to: Float32Array, size: number, radius: number, horizontal: boolean): void {
  const scale = 1 / (2 * radius + 1);
  for (let line = 0; line < size; line++) {
    let sum = 0;
    const at = (i: number) => { const c = Math.min(size - 1, Math.max(0, i)); return horizontal ? from[line * size + c] : from[c * size + line]; };
    for (let i = -radius; i <= radius; i++) sum += at(i);
    for (let i = 0; i < size; i++) {
      if (horizontal) to[line * size + i] = sum * scale; else to[i * size + line] = sum * scale;
      sum += at(i + radius + 1) - at(i - radius);
    }
  }
}

const SKY_VERTEX = /* glsl */`
varying vec3 vDirection;
void main() {
  vDirection = position;
  // Round the camera, whatever its position; at the far plane, so everything else is drawn in front.
  vec4 clip = projectionMatrix * mat4(mat3(viewMatrix)) * vec4(position, 1.0);
  gl_Position = clip.xyww;
}`;

/** The panorama lookups both meshes share. */
const COMMON = /* glsl */`
uniform sampler2D panorama;
uniform sampler2D previous;
uniform float fade;
uniform float heading;
uniform float previousHeading;
const float PI = 3.14159265359;

// Panorama coordinates of a direction: u grows clockwise on the plan from its middle, v from the zenith down.
vec2 panoramaUv(vec3 d, float offset) {
  float plan = atan(d.x, -d.z);
  return vec2(fract((plan - offset) / (2.0 * PI) + 0.5), 0.5 - asin(clamp(d.y, -1.0, 1.0)) / PI);
}

// Catmull-Rom from nine bilinear taps (after MJP's sampleTextureCatmullRom): where a texel covers more than a pixel
// it keeps the edges of roofs, fences and leaves crisp, which bilinear filtering smudges.
vec3 catmullRom(sampler2D map, vec2 uv, vec2 size) {
  vec2 at = uv * size, centre = floor(at - 0.5) + 0.5, f = at - centre;
  vec2 w0 = f * (-0.5 + f * (1.0 - 0.5 * f));
  vec2 w1 = 1.0 + f * f * (-2.5 + 1.5 * f);
  vec2 w2 = f * (0.5 + f * (2.0 - 1.5 * f));
  vec2 w3 = f * f * (-0.5 + 0.5 * f);
  vec2 w12 = w1 + w2;
  vec2 a = (centre - 1.0) / size, b = (centre + w2 / w12) / size, c = (centre + 2.0) / size;
  vec3 colour = (textureLod(map, vec2(a.x, a.y), 0.0).rgb * w0.x + textureLod(map, vec2(b.x, a.y), 0.0).rgb * w12.x
      + textureLod(map, vec2(c.x, a.y), 0.0).rgb * w3.x) * w0.y
    + (textureLod(map, vec2(a.x, b.y), 0.0).rgb * w0.x + textureLod(map, vec2(b.x, b.y), 0.0).rgb * w12.x
      + textureLod(map, vec2(c.x, b.y), 0.0).rgb * w3.x) * w12.y
    + (textureLod(map, vec2(a.x, c.y), 0.0).rgb * w0.x + textureLod(map, vec2(b.x, c.y), 0.0).rgb * w12.x
      + textureLod(map, vec2(c.x, c.y), 0.0).rgb * w3.x) * w3.y;
  return max(colour, 0.0);
}

// A lookup whose level of detail ignores the seam where u wraps; magnified, through the bicubic filter.
vec3 panoramaAt(sampler2D map, vec3 d, float offset) {
  vec2 uv = panoramaUv(d, offset);
  vec2 wrapped = vec2(fract(uv.x + 0.5), uv.y);
  vec2 dx = dFdx(uv), dy = dFdy(uv), wdx = dFdx(wrapped), wdy = dFdy(wrapped);
  dx.x = abs(dx.x) < abs(wdx.x) ? dx.x : wdx.x;
  dy.x = abs(dy.x) < abs(wdy.x) ? dy.x : wdy.x;
  vec2 size = vec2(textureSize(map, 0));
  // Texels per pixel: under one the picture is magnified, as it is through a window at 4096 px round.
  float magnified = 1.0 - smoothstep(0.75, 1.0, max(length(dx * size), length(dy * size)));
  if (magnified >= 1.0) return catmullRom(map, uv, size);
  vec3 colour = textureGrad(map, uv, dx, dy).rgb;
  return magnified > 0.0 ? mix(colour, catmullRom(map, uv, size), magnified) : colour;
}

// The view as it stands, or crossing from the one before.
vec3 viewAt(vec3 d) {
  vec3 colour = panoramaAt(panorama, d, heading);
  if (fade < 1.0) colour = mix(panoramaAt(previous, d, previousHeading), colour, fade);
  return colour;
}
`;

const SKY_FRAGMENT = /* glsl */`
varying vec3 vDirection;
void main() {
  gl_FragColor = vec4(viewAt(normalize(vDirection)), 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

const GROUND_VERTEX = /* glsl */`
varying vec3 vWorld;
void main() {
  vec4 world = modelMatrix * vec4(position, 1.0);
  vWorld = world.xyz;
  gl_Position = projectionMatrix * viewMatrix * world;
}`;

const GROUND_FRAGMENT = /* glsl */`
uniform sampler2D ground;
uniform float groundSize;
uniform float groundReady;
uniform sampler2D footprint;
uniform vec4 footprintRect;
uniform vec2 horizonBand;
uniform vec2 farBand;
varying vec3 vWorld;
const vec3 LUMA = vec3(0.2126, 0.7152, 0.0722);

// Value noise over a sine-free hash, steady at the large coordinates of far ground.
float hash(vec2 p) {
  vec3 q = fract(vec3(p.xyx) * 0.1031);
  q += dot(q, q.yzx + 33.33);
  return fract((q.x + q.y) * q.z);
}
float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p), u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), u.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x), u.y);
}

// The ground tile twice, at two scales and turns so its repeat does not show, over a slow wash of light and dark
// that fades out where it would only flicker.
vec3 tileAt(vec2 xz, float distance) {
  vec2 p = xz / groundSize;
  vec3 a = texture(ground, p).rgb;
  vec3 b = texture(ground, mat2(0.8, 0.6, -0.6, 0.8) * p * 0.43 + vec2(0.31, 0.67)).rgb;
  float wash = mix(noise(xz / 9.0) * 0.6 + noise(xz / 31.0) * 0.4, 0.5, smoothstep(60.0, 220.0, distance));
  return (a + b) * 0.5 * (0.88 + 0.24 * wash);
}

void main() {
  vec3 toSpot = vWorld - cameraPosition;
  float distance = length(toSpot);
  vec3 ray = toSpot / max(distance, 1e-4);
  // Towards the horizon the ground becomes the panorama's own far ground, read by direction as the sky is.
  float depression = asin(clamp(-ray.y, 0.0, 1.0));
  float near = smoothstep(horizonBand.x, horizonBand.y, depression) * (1.0 - smoothstep(farBand.x, farBand.y, distance));
  vec3 far = viewAt(ray);
  vec3 tile = tileAt(vWorld.xz, distance);
  vec3 colour = mix(far, tile, near * groundReady);
  // Under the house the floors stand; where the walls meet the ground the light goes.
  float inside = texture(footprint, (vWorld.xz - footprintRect.xy) * footprintRect.zw).r;
  colour *= 1.0 - 0.3 * smoothstep(0.0, 0.8, inside);
  if (inside > 0.97) discard;
  gl_FragColor = vec4(colour, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;
